import { useEffect, useRef } from 'react';
import type { Bet } from '@urbanrats/contracts';
import type { EmbedHandle } from './embed.ts';
import type { ClientGateways } from './gateway/index.ts';
import type { GameView } from './useGame.ts';

interface Local {
  muted: boolean;
  setMuted: (m: boolean) => void;
  volume: number;
  setVolume: (v: number) => void;
  panel: string | null;
  setPanel: (p: null | 'history') => void;
}

const betRef = (b: Bet) => ({ betId: b.betId, gameRoundId: b.gameRoundId, selection: b.selection, stake: b.stake });

/**
 * Connects the game screen to the embed bridge: answers parent commands from local UI state and gateway
 * reads, and reports game events. Events mirror server-confirmed state only (never animations).
 */
export function useEmbed(embed: EmbedHandle | null, gw: ClientGateways, game: GameView, local: Local): void {
  const latest = useRef({ game, local });
  useEffect(() => {
    latest.current = { game, local };
  });

  useEffect(() => {
    if (!embed) return;
    embed.attach({
      state: () => {
        const { game: g, local: l } = latest.current;
        return {
          gameId: gw.gameInfo.gameId,
          gameRoundId: g.round?.gameRoundId ?? null,
          roundState: g.round?.state ?? null,
          balance: g.balance?.balance ?? null,
          muted: l.muted,
          volume: l.volume,
          openPanel: l.panel,
          sessionExpired: g.sessionExpired,
        };
      },
      refreshBalance: async () => {
        latest.current.game.resync(); // also updates the on-screen balance
        return gw.wallet.getBalance();
      },
      // Update the mirror immediately so the command's response reports the new value (React re-renders later).
      setMuted: (m) => {
        latest.current.local.setMuted(m);
        latest.current.local = { ...latest.current.local, muted: m };
      },
      setVolume: (v) => {
        latest.current.local.setVolume(v);
        latest.current.local = { ...latest.current.local, volume: v };
      },
      openHistory: () => {
        latest.current.local.setPanel('history');
        latest.current.local = { ...latest.current.local, panel: 'history' };
      },
      closePanels: () => {
        latest.current.local.setPanel(null);
        latest.current.local = { ...latest.current.local, panel: null };
      },
    });
    return () => embed.attach(null);
  }, [embed, gw]);

  const round = game.round;
  useEffect(() => {
    if (embed && round) embed.emit('round.phase', { gameRoundId: round.gameRoundId, state: round.state });
  }, [embed, round?.gameRoundId, round?.state]); // eslint-disable-line react-hooks/exhaustive-deps

  // Bets, balance and session come straight from the private (server) event stream: each event is a
  // confirmed state change, so a reload or a history refetch never re-announces old bets.
  useEffect(() => {
    if (!embed) return;
    return gw.game.subscribePrivate((e) => {
      const p = e.payload;
      if (p.type === 'bet.updated') {
        const b = p.bet;
        if (b.state === 'ACCEPTED') embed.emit('bet.accepted', betRef(b));
        else if (b.state === 'REJECTED') embed.emit('bet.rejected', { ...betRef(b), reason: b.rejectReason });
        else if (b.state === 'SETTLED' || b.state === 'VOIDED') embed.emit('bet.result', { ...betRef(b), state: b.state, credit: b.payoutGross });
      } else if (p.type === 'balance.changed') embed.emit('balance.changed', { balance: p.balance.balance, balanceVersion: p.balance.balanceVersion });
      else if (p.type === 'session.expired') embed.emit('session-expired', {});
    });
  }, [embed, gw]);
}
